import type { StationTicket } from '@gc/shared';

import {
  wrapToWidth,
  RECEIPT_DOTS,
  type Align,
  type Block,
  type Measure,
  type ReceiptLayout,
} from './receiptLayout';

/**
 * A KITCHEN, BAR OR SHISHA TICKET — drawn, to the owner's mockup.
 *
 * The same reasoning as the receipt: boxed cells and a heavy title are not
 * expressible in the printer's own font, so the ticket is drawn and sent as
 * dots. It shares the receipt's block types and the same canvas painter; only
 * the arrangement differs.
 *
 * NO PRICES, deliberately, and none in the mockup either. The person making a
 * drink does not need the money and should not be handed it — what they need is
 * where it is going, what to make, how many, and any note.
 *
 * Pure, and takes its text measurement as a callback, for the same reason the
 * receipt's layout does: canvas code cannot be tested in this workspace, so the
 * arithmetic is kept out of it.
 */

const MARGIN = 12;
const CELL_PAD = 8;

const FONTS = {
  title: 'bold 34px Arial, sans-serif',
  client: 'bold 26px Arial, sans-serif',
  head: 'bold 23px Arial, sans-serif',
  body: '23px Arial, sans-serif',
  meta: '21px Arial, sans-serif',
} as const;

const LINE_HEIGHT = { title: 44, client: 34, head: 32, body: 32, meta: 28 } as const;

/** What the Notes column shows when an item carries none. */
const NO_NOTE = '—';

/** `DD/MM/YYYY` and `10:45 PM`, in the till's own clock. */
const pad = (value: number): string => String(value).padStart(2, '0');

export function ticketDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${at.getFullYear()}`;
}

export function ticketClock(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const hours = at.getHours();
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${pad(at.getMinutes())} ${hours < 12 ? 'AM' : 'PM'}`;
}

/**
 * The heading a runner reads first: WHERE this is going.
 *
 * A destination name is a device or a table and is the useful answer. An order
 * against the tab as a whole has none, and then the party's own name is the
 * only thing that identifies it.
 */
export function ticketTitle(ticket: StationTicket): string {
  return ticket.destination.name ? `DEVICE: ${ticket.destination.name}` : `TAB: ${ticket.tabName}`;
}

export function layoutStationTicket(ticket: StationTicket, measure: Measure): ReceiptLayout {
  const headers = ['Name', 'Qty', 'Notes'];
  const rows = ticket.items.map((item) => [item.name, `${item.quantity}`, item.note ?? NO_NOTE]);

  // Qty takes only what it needs; the rest is split between name and notes,
  // because both hold prose and both wrap.
  const qtyWidth =
    Math.max(measure('Qty', FONTS.head), ...rows.map((row) => measure(row[1]!, FONTS.body))) +
    CELL_PAD * 3;
  const rest = RECEIPT_DOTS - MARGIN * 2 - qtyWidth;
  const nameWidth = Math.round(rest * 0.55);
  const widths = [nameWidth, qtyWidth, rest - nameWidth];

  const xs: number[] = [];
  let x = MARGIN;
  for (const width of widths) {
    xs.push(x);
    x += width;
  }
  const tableX = MARGIN;
  const tableWidth = widths.reduce((sum, width) => sum + width, 0);

  const blocks: Block[] = [];
  let y = 0;

  const text = (value: string, font: string, align: Align, height: number): void => {
    blocks.push({ kind: 'text', text: value, font, align, y, height });
    y += height;
  };
  const gap = (height: number): void => {
    blocks.push({ kind: 'gap', y, height });
    y += height;
  };
  const rule = (thickness = 2, at = MARGIN, width = RECEIPT_DOTS - MARGIN * 2): void => {
    blocks.push({ kind: 'rule', y, height: thickness, x: at, width });
    y += thickness;
  };

  const rowOf = (
    cells: Array<{ text: string; x: number; width: number; align: Align }>,
    font: string,
    lineHeight: number,
    options: { header?: boolean; boxed?: boolean } = {},
  ): void => {
    const wrapped = cells.map((cell) =>
      wrapToWidth(cell.text, cell.width - CELL_PAD * 2, font, measure),
    );
    const height = Math.max(...wrapped.map((lines) => lines.length)) * lineHeight + CELL_PAD * 2;

    blocks.push({
      kind: 'row',
      y,
      height,
      header: options.header ?? false,
      x: tableX,
      width: tableWidth,
      boxed: options.boxed ?? true,
      font,
      cells: wrapped.map((lines, index) => ({
        lines,
        x: cells[index]!.x,
        width: cells[index]!.width,
        align: cells[index]!.align,
      })),
    });
    y += height;
  };

  /* ---------------------------------------------------------------- head --- */
  gap(8);
  /*
   * A RETURN says so above the destination, in the title face.
   *
   * The Qty column below already carries the minus, and that is the precise
   * record — but precision at 32 dots is not what a cook reads under a heat
   * lamp. They read the biggest thing on the paper, and if that says the same
   * as every other chit they make the burger again.
   */
  if (ticket.isReturn) {
    text('*** RETURN ***', FONTS.title, 'centre', LINE_HEIGHT.title);
    text('DO NOT MAKE', FONTS.head, 'centre', LINE_HEIGHT.head);
    gap(6);
  }
  text(ticketTitle(ticket), FONTS.title, 'centre', LINE_HEIGHT.title);
  gap(6);
  rule(3);
  gap(8);

  // Divided but not boxed, as the mockup draws it.
  rowOf(
    [
      { text: 'Client:', x: tableX, width: Math.round(tableWidth / 2), align: 'left' },
      {
        text: ticket.tabName,
        x: tableX + Math.round(tableWidth / 2),
        width: tableWidth - Math.round(tableWidth / 2),
        align: 'right',
      },
    ],
    FONTS.client,
    LINE_HEIGHT.client,
    { boxed: false },
  );
  gap(10);

  /* --------------------------------------------------------------- table --- */
  rule(2, tableX, tableWidth);
  rowOf(
    headers.map((header, index) => ({
      text: header,
      x: xs[index]!,
      width: widths[index]!,
      align: 'centre' as Align,
    })),
    FONTS.head,
    LINE_HEIGHT.head,
    { header: true },
  );
  rule(2, tableX, tableWidth);

  for (const row of rows) {
    rowOf(
      row.map((value, index) => ({
        text: value,
        x: xs[index]!,
        width: widths[index]!,
        // The item name sits left the way a label does; the count and the note
        // are centred in their cells, as drawn.
        align: (index === 0 ? 'left' : 'centre') as Align,
      })),
      FONTS.body,
      LINE_HEIGHT.body,
    );
    rule(2, tableX, tableWidth);
  }

  /*
   * The ORDER-LEVEL note, when there is one — it belongs to the whole ticket
   * rather than any line, and it is the thing most likely to be missed if it
   * were folded into a cell.
   */
  if (ticket.note) {
    gap(8);
    for (const line of wrapToWidth(
      `Note: ${ticket.note}`,
      RECEIPT_DOTS - MARGIN * 2,
      FONTS.body,
      measure,
    )) {
      text(line, FONTS.body, 'left', LINE_HEIGHT.body);
    }
  }

  /* -------------------------------------------------------------- footer --- */
  gap(12);
  rule(3);
  gap(4);
  const third = Math.round(tableWidth / 3);
  rowOf(
    [
      { text: `Cashier: ${ticket.cashierName ?? ''}`, x: tableX, width: third, align: 'left' },
      { text: ticketDate(ticket.placedAt), x: tableX + third, width: third, align: 'centre' },
      {
        text: ticketClock(ticket.placedAt),
        x: tableX + third * 2,
        width: tableWidth - third * 2,
        align: 'centre',
      },
    ],
    FONTS.meta,
    LINE_HEIGHT.meta,
    { boxed: false },
  );
  gap(16);

  return { widthDots: RECEIPT_DOTS, heightDots: y, blocks };
}
